<script setup lang="ts">
  import { onBeforeUnmount, ref, watch } from 'vue'

  import { SearchLgIcon } from '@gohighlevel/ghl-icons/24/outline'

  import { createElementId } from '@common/core-layer/utils'

  import CPInput from '@ui/components/CPInput.vue'

  import { useSearch } from '../composables/useSearch'

  const props = defineProps({
    placeholder: {
      type: String,
      default: 'Search...',
    },
    debounceMs: {
      type: Number,
      default: 300,
    },
    size: {
      type: String as () => 'xs' | 'sm' | 'md' | 'lg',
      default: 'xs',
    },
  })

  const emit = defineEmits<{
    (e: 'search', query: string): void
    (e: 'clear'): void
  }>()

  const search = useSearch()

  const { uiStore } = search

  const localQuery = ref(uiStore.query)

  const debounceTimeout = ref<NodeJS.Timeout | null>(null)

  const handleClear = () => {
    localQuery.value = ''
    uiStore.clearQuery()
    emit('clear')
  }

  // Cleanup timeout on unmount to prevent memory leaks
  onBeforeUnmount(() => {
    if (debounceTimeout.value) {
      clearTimeout(debounceTimeout.value)
    }
  })

  // Sync local query with UI store (handles external changes like navigation)
  watch(
    () => uiStore.query,
    newQuery => {
      if (localQuery.value !== newQuery) {
        localQuery.value = newQuery
      }
    },
    { immediate: true }
  )

  watch(localQuery, value => {
    // Clear existing timeout before setting a new one
    if (debounceTimeout.value) {
      clearTimeout(debounceTimeout.value)
    }

    // Store timeout ID for cleanup on unmount
    debounceTimeout.value = setTimeout(() => {
      uiStore.setQuery(value)

      if (value.trim().length > 0) {
        emit('search', value)
      } else {
        emit('clear')
      }
    }, props.debounceMs)
  })
</script>

<template>
  <!-- Flat chrome: match GoKollab top-bar search (no raised shadow). -->
  <div class="search-bar rounded-md w-full">
    <CPInput
      :id="createElementId('search', 'input', 'field')"
      v-model="localQuery"
      :prefix-icon="SearchLgIcon"
      type="text"
      :placeholder="placeholder"
      :size="size"
      clearable
      @clear="handleClear"
      @keydown.esc="handleClear"
    />
  </div>
</template>
